import React from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, BookOpen, Calculator, Clock, CheckCircle2, XCircle, RefreshCw, Calendar } from 'lucide-react';

export default function MISScoringDocPage() {
  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">MIS Scoring Logic Documentation</h1>
            <p className="text-muted-foreground">Critical reference for productivity scoring calculations</p>
          </div>
        </div>

        {/* Warning Alert */}
        <Alert variant="destructive" className="border-red-500 bg-red-50">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="text-lg font-bold">Critical Warning for AI Agents</AlertTitle>
          <AlertDescription className="mt-2">
            <p className="font-semibold">DO NOT MODIFY THE SCORING LOGIC WITHOUT EXPLICIT USER APPROVAL.</p>
            <p className="mt-1">The scoring logic has been carefully designed and tested. What may appear as a bug is often intentional behavior.</p>
          </AlertDescription>
        </Alert>

        {/* Task Status Definitions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Task Status Definitions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead>Definition</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Badge className="bg-green-100 text-green-700">Done (On Time)</Badge>
                  </TableCell>
                  <TableCell>Task completed BY or BEFORE the effective due date</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Badge className="bg-yellow-100 text-yellow-700">NDOT (Late)</Badge>
                  </TableCell>
                  <TableCell>Task completed, but AFTER the effective due date</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Badge className="bg-red-100 text-red-700">ND (Not Done)</Badge>
                  </TableCell>
                  <TableCell>Task not completed at all (by reference date)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Scoring Formulas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Scoring Formulas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg font-mono text-sm">
              <p><strong>NDOT%</strong> = 100 - (Done / Total × 100)</p>
              <p className="text-muted-foreground ml-8">= (Done Late + Not Done) / Total × 100</p>
              <p className="mt-2"><strong>ND%</strong> = Not Done / Total × 100</p>
            </div>
            
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Example Calculation:</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Metric</TableHead>
                    <TableHead className="text-center">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Total Due</TableCell>
                    <TableCell className="text-center">10</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Done (On Time)</TableCell>
                    <TableCell className="text-center">6</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Done Late</TableCell>
                    <TableCell className="text-center">2</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Not Done</TableCell>
                    <TableCell className="text-center">2</TableCell>
                  </TableRow>
                  <TableRow className="bg-blue-50">
                    <TableCell className="font-semibold">NDOT%</TableCell>
                    <TableCell className="text-center font-semibold">40%</TableCell>
                  </TableRow>
                  <TableRow className="bg-red-50">
                    <TableCell className="font-semibold">ND%</TableCell>
                    <TableCell className="text-center font-semibold">20%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Rolled Over Tasks - Critical */}
        <Card className="border-2 border-orange-300">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <RefreshCw className="w-5 h-5" />
              Rolled Over Tasks - CRITICAL LOGIC
            </CardTitle>
            <CardDescription className="text-orange-600">
              This is the most important rule - DO NOT CHANGE
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <Alert className="border-orange-500 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertTitle className="text-orange-700">Fresh Start Rule</AlertTitle>
              <AlertDescription className="text-orange-700">
                <strong>Rolled tasks get Monday 23:59:59 as the NEW effective due date.</strong>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <p><strong>What this means:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>If a rolled task is completed by Monday 23:59:59 → <Badge className="bg-green-100 text-green-700">Done (On Time)</Badge></li>
                <li>If a rolled task is completed after Monday 23:59:59 → <Badge className="bg-yellow-100 text-yellow-700">NDOT (Late)</Badge></li>
                <li>If a rolled task is not completed by week end → <Badge className="bg-red-100 text-red-700">ND (Not Done)</Badge></li>
              </ul>
            </div>

            <div className="mt-4">
              <h4 className="font-semibold mb-2">Example (Week 7 = Feb 9-15, 2026):</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Scenario</TableHead>
                    <TableHead>Original Due</TableHead>
                    <TableHead>Completed At</TableHead>
                    <TableHead>Week 7 Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-green-50">
                    <TableCell>Task rolled to Week 7</TableCell>
                    <TableCell>Jan 31, 5pm</TableCell>
                    <TableCell>Feb 9 (Mon), 9am</TableCell>
                    <TableCell><Badge className="bg-green-100 text-green-700">Done ✓</Badge></TableCell>
                  </TableRow>
                  <TableRow className="bg-yellow-50">
                    <TableCell>Task rolled to Week 7</TableCell>
                    <TableCell>Feb 5, 2pm</TableCell>
                    <TableCell>Feb 10 (Tue), 3pm</TableCell>
                    <TableCell><Badge className="bg-yellow-100 text-yellow-700">NDOT</Badge></TableCell>
                  </TableRow>
                  <TableRow className="bg-red-50">
                    <TableCell>Task rolled to Week 7</TableCell>
                    <TableCell>Feb 7, 12pm</TableCell>
                    <TableCell>Not completed</TableCell>
                    <TableCell><Badge className="bg-red-100 text-red-700">ND</Badge></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mt-4">
              <h4 className="font-semibold text-blue-700">Why This Rule Exists:</h4>
              <ol className="list-decimal list-inside space-y-1 mt-2 text-blue-700">
                <li>Employee gets Monday to complete overdue tasks</li>
                <li>Completing on Monday = On Time (no penalty)</li>
                <li>Encourages clearing backlogs at week start</li>
                <li>Fairness mechanism - don't penalize forever for past delays</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Summary vs Scoring Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Summary Metrics vs Scoring Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metric</TableHead>
                  <TableHead>Definition</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-semibold">Due This Week</TableCell>
                  <TableCell>All tasks Mon-Sun + Rolled tasks (full week view)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">Due Until Now</TableCell>
                  <TableCell>Tasks due up to current day + Rolled tasks</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">For Historical Weeks:</h4>
                <p className="text-sm">Due This Week = Due Until Now</p>
                <p className="text-sm text-muted-foreground">(Both show complete picture since week has ended)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">For Current Week (e.g., Thursday):</h4>
                <p className="text-sm">Due This Week: 10 (Mon-Sun)</p>
                <p className="text-sm">Due Until Now: 7 (Mon-Thu only)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Do's and Don'ts */}
        <Card>
          <CardHeader>
            <CardTitle>For AI Agents: Do's and Don'ts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-700 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  DO NOT
                </h4>
                <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                  <li>Change effective_due logic for rolled tasks</li>
                  <li>Use original due instead of effective due for NDOT</li>
                  <li>Modify percentage formulas without understanding</li>
                  <li>Assume a "bug" without consulting user first</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-700 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  DO
                </h4>
                <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                  <li>Always ask user before modifying scoring logic</li>
                  <li>Test changes with specific examples first</li>
                  <li>Verify calculations against this document</li>
                  <li>Preserve the "fresh start" rule for rolled tasks</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground py-4">
          <p>Last Updated: February 13, 2026</p>
          <p>Document Location: /app/memory/MIS_SCORING_LOGIC.md</p>
        </div>
      </div>
    </DashboardLayout>
  );
}
